import type { AppLanguage } from '../types';
import type { TrainingSession } from '../services/progress';
import { calculateEarnedXp } from './trainingRewards';

export interface DailyTrainingStats {
  dateKey: string;
  date: Date;
  label: string;
  minutes: number;
  verbsLearned: number;
  accuracy: number;
  sessionsCount: number;
  xp: number;
}

export type StreakMode = 'current' | 'recent';

function startOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function toDayKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatShortDate(date: Date, language: AppLanguage) {
  return date.toLocaleDateString(language === 'uk' ? 'uk-UA' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
  });
}

export function formatLongDate(date: Date, language: AppLanguage) {
  return date.toLocaleDateString(language === 'uk' ? 'uk-UA' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function calculateSessionXp(session: Pick<TrainingSession, 'mode' | 'xpEarned' | 'accuracy'>) {
  if (Number.isFinite(session.xpEarned)) {
    return session.xpEarned;
  }

  return calculateEarnedXp(session.mode, session.accuracy);
}

export function calculateTotalXp(sessions: TrainingSession[]) {
  return sessions.reduce((sum, session) => sum + calculateSessionXp(session), 0);
}

export function buildDailyStats(sessions: TrainingSession[], language: AppLanguage, days = 30): DailyTrainingStats[] {
  const today = startOfDay(new Date());
  const grouped = new Map<string, DailyTrainingStats>();

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    const dateKey = toDayKey(date);

    grouped.set(dateKey, {
      dateKey,
      date,
      label: formatShortDate(date, language),
      minutes: 0,
      verbsLearned: 0,
      accuracy: 0,
      sessionsCount: 0,
      xp: 0,
    });
  }

  sessions.forEach((session) => {
    const date = startOfDay(session.completedAt);
    const dateKey = toDayKey(date);
    const entry = grouped.get(dateKey);

    if (!entry) {
      return;
    }

    const nextAccuracySum = entry.accuracy * entry.sessionsCount + session.accuracy;
    entry.minutes += session.durationMinutes;
    entry.verbsLearned += session.verbsLearned;
    entry.sessionsCount += 1;
    entry.xp += calculateSessionXp(session);
    entry.accuracy = Math.round(nextAccuracySum / entry.sessionsCount);
  });

  return Array.from(grouped.values());
}

export function getTodayStats(sessions: TrainingSession[], language: AppLanguage) {
  return buildDailyStats(sessions, language, 1)[0] ?? {
    dateKey: toDayKey(new Date()),
    date: startOfDay(new Date()),
    label: formatShortDate(new Date(), language),
    minutes: 0,
    verbsLearned: 0,
    accuracy: 0,
    sessionsCount: 0,
    xp: 0,
  };
}

export function calculateStreak(sessions: TrainingSession[], mode: StreakMode = 'current') {
  const activeDays = Array.from(
    new Set(
      sessions
        .filter((session) => session.durationMinutes > 0)
        .map((session) => startOfDay(session.completedAt).getTime()),
    ),
  ).sort((left, right) => right - left);

  if (activeDays.length === 0) {
    return 0;
  }

  const activeDaySet = new Set(activeDays);
  const cursor = mode === 'current' ? startOfDay(new Date()) : new Date(activeDays[0]);

  let streak = 0;

  while (activeDaySet.has(cursor.getTime())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  return streak;
}

export function calculateCurrentStreak(sessions: TrainingSession[]) {
  return calculateStreak(sessions, 'current');
}

export function calculateRecentStreak(sessions: TrainingSession[]) {
  return calculateStreak(sessions, 'recent');
}

export function summarizeSessions(sessions: TrainingSession[]) {
  const totalSessions = sessions.length;
  const totalMinutes = sessions.reduce((sum, session) => sum + session.durationMinutes, 0);
  const totalVerbsLearned = sessions.reduce((sum, session) => sum + session.verbsLearned, 0);
  const totalXp = calculateTotalXp(sessions);
  const averageAccuracy =
    totalSessions > 0 ? Math.round(sessions.reduce((sum, session) => sum + session.accuracy, 0) / totalSessions) : 0;

  return {
    totalSessions,
    totalMinutes,
    totalVerbsLearned,
    totalXp,
    averageAccuracy,
    currentStreak: calculateCurrentStreak(sessions),
    recentStreak: calculateRecentStreak(sessions),
    lastTrainingAt: sessions[0]?.completedAt ?? null,
  };
}